import { useState, useEffect } from "react";
import { ShieldCheck, ShieldAlert, AlertTriangle, Info, Download, X, Loader2, Save, Pencil, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { authFetch } from "../services/authService";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function XmlValidatorDialog({ invoiceId, invoiceNumber, onClose, onDownload }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [editFields, setEditFields] = useState({});

  // Fetch invoice data for editing
  useEffect(() => {
    authFetch(`${API}/invoices`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : data.invoices || [];
        const inv = list.find(i => i.invoice_id === invoiceId);
        if (inv) {
          setInvoiceData(inv);
          setEditFields({
            customer_name: inv.customer_name || "",
            customer_fiscal_code: inv.customer_fiscal_code || "",
            customer_vat_number: inv.customer_vat_number || "",
            customer_address: inv.customer_address || "",
            customer_city: inv.customer_city || "",
            customer_province: inv.customer_province || "",
            customer_cap: inv.customer_cap || "",
            customer_sdi_code: inv.customer_sdi_code || "",
            customer_pec: inv.customer_pec || "",
          });
        }
      })
      .catch(() => {});
  }, [invoiceId]);

  const runValidation = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/invoices/${invoiceId}/validate-xml`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        if (data.errors.length > 0) setEditMode(true);
        else setEditMode(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore durante la validazione");
      }
    } catch {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const saveAndRevalidate = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/invoices/${invoiceId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editFields),
      });
      if (res.ok) {
        toast.success("Dati aggiornati");
        await runValidation();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore salvataggio");
      }
    } catch {
      toast.error("Errore di connessione");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    if (result && !result.can_download) {
      toast.error("Correggere gli errori prima di scaricare l'XML");
      return;
    }
    onDownload?.();
    onClose();
  };

  const scoreColor =
    !result ? "text-[var(--text-muted)]"
    : result.score >= 80 ? "text-green-400"
    : result.score >= 50 ? "text-yellow-400"
    : "text-red-400";

  const ScoreIcon = !result ? ShieldCheck : result.valid ? ShieldCheck : ShieldAlert;

  const updateField = (field, value) => setEditFields(prev => ({ ...prev, [field]: value }));

  // Map error fields to editable fields
  const isCustomerError = (campo) =>
    campo.toLowerCase().includes("cliente") || campo.toLowerCase().includes("cf/p.iva");
  const isWorkshopError = (campo) =>
    campo.toLowerCase().includes("officina") || campo.toLowerCase().includes("ragione sociale");

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-6">
      <div
        data-testid="xml-validator-dialog"
        className="bg-[var(--bg-secondary)] border border-[var(--border-primary)] rounded-sm w-full max-w-2xl mx-4 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-primary)]">
          <div className="flex items-center gap-2">
            <ScoreIcon className={`w-5 h-5 ${scoreColor}`} />
            <h2 className="text-base font-semibold text-[var(--text-primary)]">
              Validatore XML - {invoiceNumber}
            </h2>
          </div>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 max-h-[75vh] overflow-y-auto">
          {/* Initial state */}
          {!result && !loading && (
            <div className="text-center py-8">
              <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-[var(--text-muted)]" />
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                Verifica la qualita dell'XML prima del download.<br />
                Controlla: dati fiscali, totali, campi obbligatori.
              </p>
              <Button data-testid="run-validation-btn" className="btn-primary" onClick={runValidation}>
                Avvia Validazione
              </Button>
            </div>
          )}

          {loading && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-[var(--accent-primary)]" />
              <p className="text-sm text-[var(--text-muted)]">Analisi in corso...</p>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              {/* Score */}
              <div className="flex items-center justify-between bg-[var(--bg-primary)] p-3 rounded-sm border border-[var(--border-primary)]">
                <span className="text-sm text-[var(--text-secondary)]">Punteggio Qualita</span>
                <span className={`text-2xl font-bold ${scoreColor}`}>{result.score}/100</span>
              </div>

              {/* Errors */}
              {result.errors.length > 0 && (
                <div data-testid="validation-errors">
                  <h3 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-1">
                    <ShieldAlert className="w-4 h-4" /> Errori Bloccanti ({result.errors.length})
                  </h3>
                  <div className="space-y-1">
                    {result.errors.map((e, i) => (
                      <div key={i} className="flex gap-2 p-2 bg-red-900/15 border border-red-800/30 rounded-sm text-sm">
                        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <span className="font-medium text-red-400">{e.campo}:</span>{" "}
                          <span className="text-[var(--text-secondary)]">{e.messaggio}</span>
                          {isWorkshopError(e.campo) && (
                            <a href="/profile" className="inline-flex items-center gap-1 ml-2 text-xs text-blue-400 hover:text-blue-300">
                              <ExternalLink className="w-3 h-3" /> Vai al Profilo
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Editable customer fields when errors exist */}
              {editMode && result.errors.some(e => isCustomerError(e.campo)) && invoiceData && (
                <div data-testid="invoice-edit-section" className="border border-[var(--accent-primary)]/30 rounded-sm p-4 bg-[var(--bg-primary)]">
                  <h3 className="text-sm font-semibold text-[var(--accent-primary)] mb-3 flex items-center gap-1">
                    <Pencil className="w-4 h-4" /> Modifica Dati Cliente
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[var(--text-muted)]">Nome / Ragione Sociale</label>
                      <input data-testid="edit-customer-name" className="form-input text-sm" value={editFields.customer_name}
                        onChange={e => updateField("customer_name", e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs text-[var(--text-muted)]">Codice Fiscale</label>
                      <input data-testid="edit-customer-cf" className="form-input text-sm uppercase" value={editFields.customer_fiscal_code}
                        onChange={e => updateField("customer_fiscal_code", e.target.value.toUpperCase())} />
                    </div>
                    <div>
                      <label className="text-xs text-[var(--text-muted)]">Partita IVA</label>
                      <input data-testid="edit-customer-vat" className="form-input text-sm" value={editFields.customer_vat_number}
                        onChange={e => updateField("customer_vat_number", e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs text-[var(--text-muted)]">Indirizzo</label>
                      <input data-testid="edit-customer-address" className="form-input text-sm" value={editFields.customer_address}
                        onChange={e => updateField("customer_address", e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs text-[var(--text-muted)]">Citta</label>
                      <input data-testid="edit-customer-city" className="form-input text-sm" value={editFields.customer_city}
                        onChange={e => updateField("customer_city", e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-[var(--text-muted)]">CAP</label>
                        <input data-testid="edit-customer-cap" className="form-input text-sm" maxLength={5} value={editFields.customer_cap}
                          onChange={e => updateField("customer_cap", e.target.value)} />
                      </div>
                      <div>
                        <label className="text-xs text-[var(--text-muted)]">Prov</label>
                        <input data-testid="edit-customer-prov" className="form-input text-sm uppercase" maxLength={2} value={editFields.customer_province}
                          onChange={e => updateField("customer_province", e.target.value.toUpperCase())} />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-[var(--text-muted)]">Codice SDI</label>
                      <input className="form-input text-sm uppercase" maxLength={7} value={editFields.customer_sdi_code}
                        onChange={e => updateField("customer_sdi_code", e.target.value.toUpperCase())} />
                    </div>
                    <div>
                      <label className="text-xs text-[var(--text-muted)]">PEC</label>
                      <input className="form-input text-sm" value={editFields.customer_pec}
                        onChange={e => updateField("customer_pec", e.target.value)} />
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button data-testid="save-revalidate-btn" className="btn-primary" onClick={saveAndRevalidate} disabled={saving}>
                      <Save className="w-4 h-4 mr-1" />
                      {saving ? "Salvataggio..." : "Salva e Ri-valida"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Workshop errors link */}
              {editMode && result.errors.some(e => isWorkshopError(e.campo)) && !result.errors.some(e => isCustomerError(e.campo)) && (
                <div className="border border-yellow-800/30 rounded-sm p-4 bg-yellow-900/10 text-center">
                  <p className="text-sm text-[var(--text-secondary)] mb-2">
                    Gli errori riguardano i dati dell'officina. Correggili nel Profilo.
                  </p>
                  <a href="/profile">
                    <Button variant="outline" size="sm">
                      <ExternalLink className="w-4 h-4 mr-1" /> Vai al Profilo
                    </Button>
                  </a>
                </div>
              )}

              {/* Warnings */}
              {result.warnings.length > 0 && (
                <div data-testid="validation-warnings">
                  <h3 className="text-sm font-semibold text-yellow-400 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Avvisi ({result.warnings.length})
                  </h3>
                  <div className="space-y-1">
                    {result.warnings.map((w, i) => (
                      <div key={i} className="flex gap-2 p-2 bg-yellow-900/15 border border-yellow-800/30 rounded-sm text-sm">
                        <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium text-yellow-400">{w.campo}:</span>{" "}
                          <span className="text-[var(--text-secondary)]">{w.messaggio}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quality */}
              {result.quality.length > 0 && (
                <div data-testid="validation-quality">
                  <h3 className="text-sm font-semibold text-blue-400 mb-2 flex items-center gap-1">
                    <Info className="w-4 h-4" /> Suggerimenti ({result.quality.length})
                  </h3>
                  <div className="space-y-1">
                    {result.quality.map((q, i) => (
                      <div key={i} className="flex gap-2 p-2 bg-blue-900/10 border border-blue-800/20 rounded-sm text-sm">
                        <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium text-blue-400">{q.campo}:</span>{" "}
                          <span className="text-[var(--text-secondary)]">{q.messaggio}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All clear */}
              {result.valid && result.warnings.length === 0 && result.quality.length === 0 && (
                <div className="text-center py-4 text-green-400">
                  <ShieldCheck className="w-10 h-10 mx-auto mb-2" />
                  <p className="font-medium">XML pronto per il download</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-4 border-t border-[var(--border-primary)]">
          <Button variant="outline" size="sm" onClick={onClose}>Chiudi</Button>
          <div className="flex gap-2">
            {result && (
              <Button variant="outline" size="sm" onClick={runValidation} disabled={loading}>
                Ri-valida
              </Button>
            )}
            <Button
              data-testid="xml-download-btn"
              className="btn-primary"
              onClick={handleDownload}
              disabled={!result || !result.can_download}
            >
              <Download className="w-4 h-4 mr-1" />
              {!result ? "Valida prima" : result.can_download ? "Scarica XML" : "Errori da correggere"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
